import { Link } from 'react-router-dom';
import { Calendar, Users, MapPin, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const AGE_LABELS = {
  'enfants': 'Enfants',
  'ados': 'Ados',
  'adultes': 'Adultes',
  'tous': 'Tout public'
};

const TYPE_LABELS = {
  'jeu': 'Jeu de société',
  'lecture': 'Lecture',
  'tcg': 'TCG',
  'atelier': 'Atelier',
  'autre': 'Événement'
};

const TYPE_COLORS = {
  'jeu': 'bg-[#48b4ff] text-white',
  'lecture': 'bg-purple-500 text-white',
  'tcg': 'bg-[#db3b17] text-white',
  'atelier': 'bg-green-500 text-white',
  'autre': 'bg-[#645E55] text-white'
};

export const EventCard = ({ event, featured = false }) => {
  const eventDate = new Date(event.date);
  const placesRestantes = event.max_participants - event.current_participants;
  
  return (
    <Link 
      to={`/evenement/${event.event_id}`}
      className={`event-card block h-full ${featured ? 'md:col-span-2 lg:col-span-1' : ''}`}
      data-testid={`event-card-${event.event_id}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={event.image_url || 'https://images.unsplash.com/photo-1758687126183-74fde92c6338?crop=entropy&cs=srgb&fm=jpg&q=85'}
          alt={event.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        
        {/* Badges overlay */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          <Badge className={`${TYPE_COLORS[event.event_type] || TYPE_COLORS.autre}`}>
            {TYPE_LABELS[event.event_type] || event.event_type}
          </Badge>
        </div>
        
        <div className="absolute top-3 right-3">
          {event.is_free ? (
            <Badge className="bg-green-500 text-white font-bold">Gratuit</Badge>
          ) : (
            <Badge className="bg-[#ffd451] text-[#2D2A26] font-bold">{event.price}€</Badge>
          )}
        </div>
        
        {/* Full badge */}
        {event.is_full && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="text-white font-bold text-xl uppercase tracking-wider">Complet</span>
          </div>
        )}
        
        {/* Cancelled badge */}
        {event.is_cancelled && (
          <div className="absolute inset-0 bg-red-500/80 flex items-center justify-center">
            <span className="text-white font-bold text-xl uppercase tracking-wider">Annulé</span>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-5">
        {/* Date */}
        <div className="flex items-center gap-2 text-[#db3b17] font-semibold mb-2">
          <Calendar className="w-4 h-4" />
          <span className="text-sm">
            {format(eventDate, "EEEE d MMMM 'à' HH'h'mm", { locale: fr })}
          </span>
        </div>
        
        {/* Title */}
        <h3 className="font-heading text-xl font-bold text-[#2D2A26] mb-2 line-clamp-2 group-hover:text-[#db3b17] transition-colors">
          {event.name}
        </h3>
        
        {/* Short description */}
        {event.short_description && (
          <p className="text-[#645E55] text-sm mb-4 line-clamp-2">
            {event.short_description}
          </p>
        )}
        
        {/* Meta info */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-[#645E55]">
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span>{placesRestantes > 0 ? `${placesRestantes} places` : 'Complet'}</span>
          </div>
          
          <Badge variant="secondary" className="bg-[#E5E0D8] text-[#645E55] font-medium">
            {AGE_LABELS[event.age_group] || event.age_group}
          </Badge>
        </div>
      </div>
    </Link>
  );
};

export default EventCard;
